import { z } from "zod";
/**
 * Learning objectives — the noun the teacher speaks (ADR-011).
 *
 * This is the piece that turns a pile of games into a teaching product. A teacher of
 * five-year-olds does not think "level seven of the pattern game"; they think "counting to
 * ten" and "repeating patterns". So an objective is the unit that is assigned, tracked and
 * recommended, and a game level is merely where it happens to be practised.
 *
 * The mapping runs objective → (task, level window). One game therefore carries several
 * objectives, stacked up its difficulty ladder, which is why raising the difficulty ceilings
 * was structural rather than cosmetic: each new band of levels is a new teachable objective.
 *
 * Objectives are DATA and they live in the content bundle, next to the tasks they refer to.
 * They are versioned with it and pinned with it, for the same reason: a child's history is
 * only readable against the objective set it was recorded under.
 */
/**
 * The area of the week an objective belongs to.
 *
 * Named for what a preschool teacher would call it, not for a psychological construct. This
 * is a grouping for a screen, and it makes no claim about anything.
 */
export const StrandSchema = z.enum([
    "number",
    "letters",
    "pattern",
    "logic",
    "space",
    "memory",
    "focus",
]);
/** Where an objective is practised: one game, and the band of levels that trains it. */
export const ObjectiveStepSchema = z
    .object({
    taskId: z.string().min(1),
    from: z.number().int().nonnegative(),
    to: z.number().int().nonnegative(),
})
    .refine((s) => s.to >= s.from, {
    message: "an objective's level window must not run backwards",
});
export const ObjectiveSchema = z.object({
    id: z.string().regex(/^[a-z0-9-]+$/),
    strand: StrandSchema,
    /**
     * Where it is practised. More than one step is allowed and means the same objective is
     * reachable through more than one game — which is how a child who refuses one activity can
     * still work on the thing it was there for.
     */
    steps: z.array(ObjectiveStepSchema).min(1),
    /**
     * Objectives that ordinarily come first.
     *
     * **Not a lock.** It orders what gets SUGGESTED and nothing else. A teacher knows their
     * class and we do not; the first time one of them tries to assign something early and the
     * product refuses, they stop trusting the whole thing. Requirements that cannot be
     * overridden are a guess enforced against the only person in the room with real knowledge.
     */
    requires: z.array(z.string()).default([]),
});
/**
 * How far a student has got with one objective.
 *
 * Three states, not a percentage. "Sixty percent of the way through counting to five" is a
 * number with no referent — there is no sixtieth of an idea — and it would invite exactly the
 * false precision the product spent its measurement era learning to avoid.
 *
 * **Mastery is `stepUpAfter` correct answers in a row AT THE TOP LEVEL of the window**, where
 * `stepUpAfter` is the task's own step-up rule. Two decisions are packed into that:
 *
 *   - The threshold is not a field on the objective. It is read off the task, so the rule that
 *     advances a child mid-game and the rule that marks the objective done are one rule and
 *     cannot drift apart.
 *   - It is the top level, not anywhere in the window. "Adding to ten" spans two levels; three
 *     correct answers at the easier one shows the child can do the easier one. The ladder only
 *     reaches the top by climbing, so this is also the cheapest possible check.
 */
export const ObjectiveStateSchema = z.enum(["mastered", "practising", "untouched"]);
export const ObjectiveProgressSchema = z.object({
    objectiveId: z.string().min(1),
    state: ObjectiveStateSchema,
    /** Scored trials recorded inside the objective's window. */
    trials: z.number().int().nonnegative(),
    correct: z.number().int().nonnegative(),
    /** Longest run of correct answers at the window's top level. */
    bestStreak: z.number().int().nonnegative(),
    /** The run length that would count as mastered — the task's own `stepUpAfter`. */
    needStreak: z.number().int().positive(),
    lastPlayedAt: z.string().datetime().nullable(),
    /** Set when a teacher has asked for this one, so the dashboard can say so. */
    assignmentId: z.string().uuid().nullable(),
});
/**
 * Why the system is suggesting this activity next.
 *
 * Every suggestion carries one. A recommendation a teacher cannot interrogate is one they
 * override once and then stop reading, and at that point the loop is broken at its last step.
 */
export const NextReasonSchema = z.enum([
    /** The teacher assigned it. Always wins. */
    "assigned",
    /** Started, not finished, still warm. */
    "unfinished",
    /** The one before it was learnt. */
    "progression",
    /** Nothing played yet — the entry point of the thinnest strand. */
    "starting",
    /** Everything available has been mastered recently. */
    "all-caught-up",
]);
export const NextActivitySchema = z.object({
    /** Null only with reason `all-caught-up`. */
    objectiveId: z.string().min(1).nullable(),
    taskId: z.string().min(1).nullable(),
    /** The level to open the game at, so a child never replays what they already know. */
    startAt: z.number().int().nonnegative().nullable(),
    reason: NextReasonSchema,
});
//# sourceMappingURL=objectives.js.map